import { useState, type FormEvent } from 'react';
import { submissionTips } from '@/mocks/site';

const FORM_URL = 'https://readdy.ai/api/form/dahh5jroh653ivfvnv90';

type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error';

export default function SubmitSection() {
  const [status, setStatus] = useState<SubmitStatus>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('contact_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      setFormError('');
      form.reset();
      return;
    }
    formData.delete('contact_alt');

    setStatus('submitting');
    setFormError('');

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        body.append(key, String(value));
      });

      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMessage =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = String(serverMessage || '').toLowerCase().includes('spam');

      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setFormError(serverMessage || '提交没有成功，请稍后再试一次。');
      }
    } catch {
      setStatus('error');
      setFormError('网络好像不太稳定，请检查后重新提交。');
    }
  };

  return (
    <section id="submit" className="scroll-mt-24 py-14 md:py-20">
      <div className="mx-auto w-full px-4 md:px-6 lg:px-10">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] lg:gap-14">
          <div>
            <p className="flex items-center gap-2 text-xs font-semibold tracking-[0.24em] text-primary-600">
              <span className="h-1 w-6 rounded-full bg-primary-500" aria-hidden="true"></span>
              爆料投稿
            </p>
            <h2 className="mt-3 font-heading text-2xl font-black leading-tight text-foreground-950 md:text-3xl">
              你身边的事，也许正被全校围观
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
              无论是食堂新窗口的排队奇观，还是选课系统的一次“意外”，都欢迎写下来告诉我们。我们会核对信息，并把可公开的部分整理成事件时间线。
            </p>

            <div className="mt-8 rounded-lg bg-accent-50 p-5 md:p-6">
              <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-lightbulb-line text-lg leading-none text-accent-700" aria-hidden="true"></i>
                投稿小贴士
              </h3>
              <ul className="mt-4 space-y-3 text-sm text-foreground-700">
                {submissionTips.map((tip) => (
                  <li key={tip} className="flex gap-2.5">
                    <i
                      className="ri-check-double-line mt-0.5 text-base leading-none text-primary-500"
                      aria-hidden="true"
                    ></i>
                    {tip}
                  </li>
                ))}
              </ul>
            </div>

            <p className="mt-6 flex items-center gap-2 text-sm text-foreground-500">
              <i className="ri-shield-check-line text-base leading-none text-primary-500" aria-hidden="true"></i>
              我们不会公开任何可定位到个人的信息。
            </p>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
            <form id="campus-submit-form" data-readdy-form="campus-submit" onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label htmlFor="topicType" className="mb-2 block text-sm font-medium text-foreground-800">
                    投稿类型
                  </label>
                  <select
                    id="topicType"
                    name="topic_type"
                    defaultValue="campus-life"
                    className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  >
                    <option value="campus-life">校园生活</option>
                    <option value="study-exam">学业考场</option>
                    <option value="dorm-daily">宿舍日常</option>
                    <option value="club-activity">社团活动</option>
                    <option value="campus-food">校园美食</option>
                    <option value="heart-station">情感小站</option>
                    <option value="career-intern">实习求职</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="campusName" className="mb-2 block text-sm font-medium text-foreground-800">
                    校园名称
                  </label>
                  <input
                    id="campusName"
                    name="campus"
                    type="text"
                    required
                    placeholder="例如：云栖大学"
                    className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  />
                </div>

                <div>
                  <label htmlFor="contactWay" className="mb-2 block text-sm font-medium text-foreground-800">
                    联系方式（选填）
                  </label>
                  <input
                    id="contactWay"
                    name="contact"
                    type="text"
                    placeholder="邮箱或昵称，方便后续沟通"
                    className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  />
                </div>

                <div className="sm:col-span-2">
                  <label htmlFor="eventTitle" className="mb-2 block text-sm font-medium text-foreground-800">
                    事件标题
                  </label>
                  <input
                    id="eventTitle"
                    name="event_title"
                    type="text"
                    required
                    placeholder="一句话说清这件事"
                    className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  />
                </div>

                <div className="sm:col-span-2">
                  <label htmlFor="eventDetail" className="mb-2 block text-sm font-medium text-foreground-800">
                    事件描述
                  </label>
                  <textarea
                    id="eventDetail"
                    name="detail"
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="时间、地点、经过，越具体越好（500 字以内）"
                    className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  ></textarea>
                  <p className="mt-1.5 text-xs text-foreground-400">最多 500 字，请勿填写他人隐私信息。</p>
                </div>
              </div>

              <div className="entry-guard" aria-hidden="true">
                <label htmlFor="contactAlt">联系人备用字段</label>
                <input
                  id="contactAlt"
                  name="contact_alt"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              {status === 'error' && formError && (
                <p className="mt-5 flex items-start gap-2 rounded-md bg-primary-50 px-3.5 py-3 text-sm text-primary-800">
                  <i className="ri-error-warning-line mt-0.5 text-base leading-none" aria-hidden="true"></i>
                  {formError}
                </p>
              )}

              {status === 'success' && (
                <p className="mt-5 flex items-start gap-2 rounded-md bg-secondary-100 px-3.5 py-3 text-sm text-secondary-900">
                  <i className="ri-checkbox-circle-line mt-0.5 text-base leading-none" aria-hidden="true"></i>
                  投稿已收到，谢谢你的分享！我们会在三个工作日内核对信息。
                </p>
              )}

              <button
                type="submit"
                disabled={status === 'submitting'}
                className="mt-6 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                {status === 'submitting' ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-base leading-none" aria-hidden="true"></i>
                    正在提交
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line text-base leading-none" aria-hidden="true"></i>
                    提交投稿
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}